import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { sitesService, getErrorMessage } from '../services/api';
import { 
  Loader2, 
  AlertCircle,
  RefreshCw,
  Activity,
  Zap,
  AlertTriangle,
  TrendingUp,
  Battery,
  Server,
  Settings2,
  Gauge,
  Download,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// PDF Download function
const downloadEnergyPDF = async (month, year, siteId = null) => {
  const token = sessionStorage.getItem('hims_token') || localStorage.getItem('hims_token');
  let url = `${API_URL}/api/energy/report/pdf?month=${month}&year=${year}`;
  if (siteId) url += `&site_id=${siteId}`;
  
  try {
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (!response.ok) throw new Error('PDF indirilemedi');
    
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `hims-enerji-raporu-${year}-${String(month).padStart(2, '0')}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
    a.remove();
    
    toast.success('PDF indirildi');
  } catch (err) {
    toast.error('PDF indirme hatası');
  }
};

// Custom hook for energy API calls
const useEnergyApi = () => {
  const getToken = () => localStorage.getItem('hims_token');
  
  const request = async (path, options = {}) => {
    const token = getToken();
    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });
    
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'API Error');
    }
    
    return response.json();
  };
  
  return {
    getSiteEnergy: (siteId) => request(`/api/energy/site/${siteId}`),
    getAllSitesEnergy: () => request('/api/energy/sites'),
    balanceLoad: (data) => request('/api/energy/balance', {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    checkCapacity: (siteId, stationId, power) => {
      let url = `/api/energy/check-capacity?site_id=${siteId}&station_id=${stationId}`;
      if (power) url += `&requested_power=${power}`;
      return request(url, { method: 'POST' });
    }
  };
};

// Native Button Component
const NativeButton = ({ children, onClick, type = 'button', variant = 'primary', disabled, className = '', ...props }) => {
  const baseStyle = {
    padding: '8px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    border: 'none',
    transition: 'all 0.2s',
    opacity: disabled ? 0.6 : 1
  };

  const variants = {
    primary: { backgroundColor: '#2563eb', color: 'white' },
    outline: { backgroundColor: 'transparent', color: '#374151', border: '1px solid #d1d5db' }
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{ ...baseStyle, ...variants[variant] }}
      className={className}
      {...props}
    >
      {children}
    </button>
  );
};

// Native Card Component
const NativeCard = ({ children, className = '', style = {} }) => (
  <div
    style={{
      backgroundColor: 'var(--card-bg)',
      borderRadius: '12px',
      border: '1px solid var(--card-border)',
      overflow: 'hidden',
      ...style
    }}
    className={className}
  >
    {children}
  </div>
);

// Native Badge Component
const NativeBadge = ({ children, variant = 'default' }) => {
  const variants = {
    default: { backgroundColor: '#2563eb', color: 'white' },
    success: { backgroundColor: '#10b981', color: 'white' },
    warning: { backgroundColor: '#f59e0b', color: 'white' },
    danger: { backgroundColor: '#ef4444', color: 'white' },
    secondary: { backgroundColor: '#6b7280', color: 'white' },
    outline: { backgroundColor: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--card-border)' }
  };

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 10px',
      borderRadius: '9999px',
      fontSize: '12px',
      fontWeight: '500',
      ...variants[variant]
    }}>
      {children}
    </span>
  );
};

// Native Alert Component
const NativeAlert = ({ children, variant = 'default', icon: Icon }) => {
  const variants = {
    default: { backgroundColor: '#f3f4f6', borderColor: '#d1d5db', color: '#374151' },
    warning: { backgroundColor: '#fffbeb', borderColor: '#fcd34d', color: '#92400e' },
    danger: { backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '16px',
      borderRadius: '8px',
      border: `1px solid ${variants[variant].borderColor}`,
      backgroundColor: variants[variant].backgroundColor,
      color: variants[variant].color
    }}>
      {Icon && <Icon style={{ width: '20px', height: '20px', flexShrink: 0, marginTop: '2px' }} />}
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
};

export default function EnergyPage() {
  const { isAdmin } = useAuth();
  const energyApi = useEnergyApi();
  
  const [sites, setSites] = useState([]);
  const [siteEnergyData, setSiteEnergyData] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [balancing, setBalancing] = useState(false);
  const [balanceStrategy, setBalanceStrategy] = useState('equal');
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sitesData = await sitesService.list();
      setSites(sitesData || []);
      
      const energyData = await energyApi.getAllSitesEnergy();
      setSiteEnergyData(energyData || []);
      
      if (!selectedSiteId && sitesData?.length > 0) {
        setSelectedSiteId(sitesData[0].id);
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [selectedSiteId]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(fetchData, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, fetchData]);

  const handleBalanceLoad = async () => {
    if (!selectedSiteId) {
      toast.error('Lütfen bir site seçin');
      return;
    }
    
    setBalancing(true);
    try {
      const result = await energyApi.balanceLoad({
        site_id: selectedSiteId,
        strategy: balanceStrategy
      });
      
      if (result.success) {
        toast.success(result.message);
        fetchData();
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBalancing(false);
    }
  };

  const getSelectedSiteEnergy = () => {
    return siteEnergyData.find(s => s.site_id === selectedSiteId);
  };

  const getLoadColor = (percentage) => {
    if (percentage >= 90) return '#ef4444';
    if (percentage >= 70) return '#f97316';
    if (percentage >= 50) return '#eab308';
    return '#22c55e';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'charging':
        return <NativeBadge variant="default">Şarj Ediyor</NativeBadge>;
      case 'available':
        return <NativeBadge variant="success">Müsait</NativeBadge>;
      case 'offline':
        return <NativeBadge variant="secondary">Çevrimdışı</NativeBadge>;
      case 'maintenance':
        return <NativeBadge variant="outline">Bakımda</NativeBadge>;
      default:
        return <NativeBadge variant="secondary">{status}</NativeBadge>;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <Loader2 style={{ width: '32px', height: '32px', color: '#2563eb', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <NativeAlert variant="danger" icon={AlertCircle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{error}</span>
          <NativeButton variant="outline" onClick={fetchData}>
            <RefreshCw style={{ width: '16px', height: '16px' }} />
            Tekrar Dene
          </NativeButton>
        </div>
      </NativeAlert>
    );
  }

  const selectedSiteEnergy = getSelectedSiteEnergy();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="energy-page">
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, color: 'var(--table-text)' }}>Enerji Yönetimi</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--table-text)', opacity: 0.7 }}>Yük dengeleme ve güç dağılımını yönetin</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: 'var(--table-text)' }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ width: '16px', height: '16px', borderRadius: '4px' }}
            />
            Otomatik Yenile
          </label>
          <NativeButton 
            variant="outline" 
            onClick={() => {
              const now = new Date();
              downloadEnergyPDF(now.getMonth() + 1, now.getFullYear(), selectedSiteId || null);
            }}
            title="Aylık enerji raporunu PDF olarak indir"
          >
            <Download style={{ width: '16px', height: '16px' }} />
            PDF Rapor
          </NativeButton>
          <NativeButton variant="outline" onClick={fetchData}>
            <RefreshCw style={{ width: '16px', height: '16px' }} />
            Yenile
          </NativeButton>
        </div>
      </div>

      {/* Site Selector Card */}
      <NativeCard>
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
            {/* Site Select */}
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: 'var(--table-text)' }}>
                Site Seçin
              </label>
              <select
                value={selectedSiteId}
                onChange={(e) => setSelectedSiteId(e.target.value)}
                data-testid="site-select"
                style={{
                  width: '100%',
                  height: '40px',
                  padding: '0 12px',
                  borderRadius: '8px',
                  border: '1px solid var(--card-border)',
                  fontSize: '14px',
                  backgroundColor: 'var(--card-bg)',
                  color: 'var(--table-text)',
                  cursor: 'pointer'
                }}
              >
                <option value="">Site seçin...</option>
                {sites.map(site => (
                  <option key={site.id} value={site.id}>
                    {site.name} ({site.max_power_kw || 100} kW)
                  </option>
                ))}
              </select>
            </div>
            
            {/* Strategy Select */}
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: 'var(--table-text)' }}>
                Dengeleme Stratejisi
              </label>
              <select
                value={balanceStrategy}
                onChange={(e) => setBalanceStrategy(e.target.value)}
                data-testid="strategy-select"
                style={{
                  width: '100%',
                  height: '40px',
                  padding: '0 12px',
                  borderRadius: '8px',
                  border: '1px solid var(--card-border)',
                  fontSize: '14px',
                  backgroundColor: 'var(--card-bg)',
                  color: 'var(--table-text)',
                  cursor: 'pointer'
                }}
              >
                <option value="equal">Eşit Dağılım</option>
                <option value="proportional">Kapasite Oranı</option>
                <option value="priority">Öncelik (İlk Gelen)</option>
              </select>
            </div>
            
            {/* Balance Button */}
            <NativeButton
              onClick={handleBalanceLoad}
              disabled={balancing || !selectedSiteId}
              data-testid="balance-button"
            >
              {balancing ? (
                <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
              ) : (
                <Settings2 style={{ width: '16px', height: '16px' }} />
              )}
              Yükü Dengele
            </NativeButton>
          </div>
        </div>
      </NativeCard>

      {/* Site Energy Overview */}
      {selectedSiteEnergy && (
        <>
          {/* Capacity Alerts */}
          {selectedSiteEnergy.is_overloaded && (
            <NativeAlert variant="danger" icon={AlertTriangle}>
              <strong>Dikkat!</strong> Site kapasitesi aşıldı! Yük dengeleme yapmanız önerilir.
            </NativeAlert>
          )}
          
          {selectedSiteEnergy.load_percentage >= 80 && !selectedSiteEnergy.is_overloaded && (
            <NativeAlert variant="warning" icon={AlertTriangle}>
              Site yükü %80'in üzerinde. Yeni şarj başlatmadan önce kapasite kontrolü yapılması önerilir.
            </NativeAlert>
          )}

          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            {/* Max Capacity */}
            <NativeCard>
              <div style={{ padding: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ fontSize: '14px', margin: 0, color: 'var(--table-text)', opacity: 0.7 }}>Maksimum Kapasite</p>
                    <p style={{ fontSize: '28px', fontWeight: 'bold', margin: '4px 0 0', color: 'var(--table-text)' }}>
                      {selectedSiteEnergy.max_power_kw} kW
                    </p>
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '9999px' }}>
                    <Battery style={{ width: '24px', height: '24px', color: '#6b7280' }} />
                  </div>
                </div>
              </div>
            </NativeCard>

            {/* Current Usage */}
            <NativeCard>
              <div style={{ padding: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ fontSize: '14px', margin: 0, color: 'var(--table-text)', opacity: 0.7 }}>Anlık Tüketim</p>
                    <p style={{ fontSize: '28px', fontWeight: 'bold', margin: '4px 0 0', color: '#2563eb' }}>
                      {selectedSiteEnergy.current_power_kw} kW
                    </p>
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#eff6ff', borderRadius: '9999px' }}>
                    <Zap style={{ width: '24px', height: '24px', color: '#2563eb' }} />
                  </div>
                </div>
              </div>
            </NativeCard>

            {/* Available */}
            <NativeCard>
              <div style={{ padding: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ fontSize: '14px', margin: 0, color: 'var(--table-text)', opacity: 0.7 }}>Kullanılabilir</p>
                    <p style={{ fontSize: '28px', fontWeight: 'bold', margin: '4px 0 0', color: '#16a34a' }}>
                      {selectedSiteEnergy.available_power_kw} kW
                    </p>
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#f0fdf4', borderRadius: '9999px' }}>
                    <TrendingUp style={{ width: '24px', height: '24px', color: '#16a34a' }} />
                  </div>
                </div>
              </div>
            </NativeCard>

            {/* Active Stations */}
            <NativeCard>
              <div style={{ padding: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ fontSize: '14px', margin: 0, color: 'var(--table-text)', opacity: 0.7 }}>Aktif İstasyon</p>
                    <p style={{ fontSize: '28px', fontWeight: 'bold', margin: '4px 0 0', color: '#9333ea' }}>
                      {selectedSiteEnergy.active_stations} / {selectedSiteEnergy.total_stations}
                    </p>
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#faf5ff', borderRadius: '9999px' }}>
                    <Server style={{ width: '24px', height: '24px', color: '#9333ea' }} />
                  </div>
                </div>
              </div>
            </NativeCard>
          </div>

          {/* Load Progress Bar */}
          <NativeCard>
            <div style={{ padding: '20px', borderBottom: '1px solid var(--card-border)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--table-text)' }}>
                <Gauge style={{ width: '20px', height: '20px' }} />
                Kapasite Kullanımı
              </h3>
              <p style={{ fontSize: '14px', margin: '4px 0 0', color: 'var(--table-text)', opacity: 0.7 }}>
                {selectedSiteEnergy.site_name} - Anlık güç dağılımı
              </p>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '8px' }}>
                <span style={{ color: 'var(--table-text)' }}>
                  Kullanım: {selectedSiteEnergy.current_power_kw} / {selectedSiteEnergy.max_power_kw} kW
                </span>
                <span style={{ fontWeight: '600', color: getLoadColor(selectedSiteEnergy.load_percentage) }}>
                  %{selectedSiteEnergy.load_percentage}
                </span>
              </div>
              <div style={{ height: '16px', backgroundColor: '#e5e7eb', borderRadius: '9999px', overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${Math.min(selectedSiteEnergy.load_percentage, 100)}%`,
                    backgroundColor: getLoadColor(selectedSiteEnergy.load_percentage),
                    transition: 'width 0.5s ease'
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginTop: '8px', color: 'var(--table-text)', opacity: 0.7 }}>
                <span>0 kW</span>
                <span style={{ color: '#f97316' }}>%70</span>
                <span style={{ color: '#ef4444' }}>%90</span>
                <span>{selectedSiteEnergy.max_power_kw} kW</span>
              </div>
            </div>
          </NativeCard>

          {/* Station Power Distribution Table */}
          <NativeCard>
            <div style={{ padding: '20px', borderBottom: '1px solid var(--card-border)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--table-text)' }}>
                <Activity style={{ width: '20px', height: '20px' }} />
                İstasyon Güç Dağılımı
              </h3>
              <p style={{ fontSize: '14px', margin: '4px 0 0', color: 'var(--table-text)', opacity: 0.7 }}>
                Her istasyonun anlık güç tüketimi ve tahsis edilen kapasite
              </p>
            </div>
            <div style={{ padding: '0' }}>
              {selectedSiteEnergy.stations?.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px', color: 'var(--table-text)', opacity: 0.7 }}>
                  <Server style={{ width: '48px', height: '48px', margin: '0 auto 12px', color: '#d1d5db' }} />
                  <p>Bu sitede istasyon bulunamadı</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid var(--card-border)' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--table-text)', opacity: 0.7 }}>İstasyon</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: 'var(--table-text)', opacity: 0.7 }}>Max Kapasite</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: 'var(--table-text)', opacity: 0.7 }}>Tahsis Edilen</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: 'var(--table-text)', opacity: 0.7 }}>Anlık Güç</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', minWidth: '150px', color: 'var(--table-text)', opacity: 0.7 }}>Güç Dağılımı</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--table-text)', opacity: 0.7 }}>Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSiteEnergy.stations.map((station, index) => (
                        <tr 
                          key={station.station_id} 
                          data-testid={`station-row-${index}`}
                          style={{ borderBottom: '1px solid var(--card-border)' }}
                          className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                        >
                          <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500', color: 'var(--table-text)' }}>
                            {station.station_name}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: '14px', textAlign: 'right', color: 'var(--table-text)' }}>
                            {station.power_capacity_kw} kW
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: '14px', textAlign: 'right', color: station.status === 'charging' ? '#2563eb' : 'var(--table-text)', fontWeight: station.status === 'charging' ? '600' : undefined }}>
                            {station.allocated_power_kw || station.power_capacity_kw} kW
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: '14px', textAlign: 'right', color: station.current_power_kw > 0 ? '#16a34a' : '#9ca3af', fontWeight: station.current_power_kw > 0 ? '600' : undefined }}>
                            {station.current_power_kw} kW
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ flex: 1, height: '8px', backgroundColor: '#e5e7eb', borderRadius: '9999px', overflow: 'hidden' }}>
                                <div
                                  style={{
                                    height: '100%',
                                    width: `${station.utilization_percent}%`,
                                    backgroundColor: station.status === 'charging' ? '#2563eb' : '#d1d5db'
                                  }}
                                />
                              </div>
                              <span style={{ fontSize: '12px', width: '36px', textAlign: 'right', color: 'var(--table-text)', opacity: 0.7 }}>
                                {station.utilization_percent}%
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            {getStatusBadge(station.status)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </NativeCard>
        </>
      )}

      {/* All Sites Overview */}
      {!selectedSiteId && siteEnergyData.length > 0 && (
        <NativeCard>
          <div style={{ padding: '20px', borderBottom: '1px solid var(--card-border)' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0, color: 'var(--table-text)' }}>Tüm Siteler Özeti</h3>
            <p style={{ fontSize: '14px', margin: '4px 0 0', color: 'var(--table-text)', opacity: 0.7 }}>Site bazında kapasite kullanımı</p>
          </div>
          <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {siteEnergyData.map(site => (
                <div
                  key={site.site_id}
                  onClick={() => setSelectedSiteId(site.site_id)}
                  style={{
                    padding: '16px',
                    border: '1px solid var(--card-border)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Server style={{ width: '20px', height: '20px', color: 'var(--table-text)', opacity: 0.7 }} />
                      <span style={{ fontWeight: '500', color: 'var(--table-text)' }}>{site.site_name}</span>
                      {site.is_overloaded && (
                        <NativeBadge variant="danger">Aşırı Yük!</NativeBadge>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '14px', color: 'var(--table-text)' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Zap style={{ width: '16px', height: '16px', color: '#2563eb' }} />
                        {site.current_power_kw} / {site.max_power_kw} kW
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Activity style={{ width: '16px', height: '16px', color: '#16a34a' }} />
                        {site.active_stations} / {site.total_stations} aktif
                      </span>
                    </div>
                  </div>
                  <div style={{ height: '8px', backgroundColor: '#e5e7eb', borderRadius: '9999px', overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${Math.min(site.load_percentage, 100)}%`,
                        backgroundColor: getLoadColor(site.load_percentage),
                        transition: 'width 0.3s ease'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginTop: '8px', color: 'var(--table-text)', opacity: 0.7 }}>
                    <span>%{site.load_percentage} kullanım</span>
                    <span>{site.available_power_kw} kW kullanılabilir</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </NativeCard>
      )}

      {/* No Sites Message */}
      {sites.length === 0 && (
        <NativeCard>
          <div style={{ padding: '48px', textAlign: 'center', color: 'var(--table-text)', opacity: 0.7 }}>
            <Server style={{ width: '48px', height: '48px', margin: '0 auto 12px', color: '#d1d5db' }} />
            <p>Henüz site tanımlanmamış</p>
          </div>
        </NativeCard>
      )}
    </div>
  );
}
